'use client'

import { useQuery } from '@tanstack/react-query'
import { useLocale, useTranslations } from 'next-intl'
import NoQueryResult from 'component/block/no-content/query-result'
import { TEMP_USER_ID } from 'constant'
import { fetchSearchCommunities } from 'server-actions/fetch-search'
import { useIpLocationStore } from 'store/server-context/ip-location'
import { useSessionStore } from 'store/server-context/session'
import Link from 'next/link'
import SearchItem from '../dropdown-card/search-item'
import { Image } from 'component/ui/image'
import { type IImageAssetV2 } from 'type/asset'
import IconCommunity from 'asset/icon/home/us_s.svg'
import LoadingSpinner from 'component/block/loading/spinner'

interface ICommunityResult {
  queryKeywords: string
}
const CommunityResult = ({ queryKeywords }: ICommunityResult) => {
  const t = useTranslations()
  const locale = useLocale()
  const session = useSessionStore(state => state.session)
  const ipLocation = useIpLocationStore(state => state.ipLocation)

  const { data, isLoading } = useQuery({
    queryKey: ['searchResultCommunity', queryKeywords],
    queryFn: async () => {
      let lat = '0'
      let lng = '0'

      if (ipLocation) {
        const {
          ip_geo_point: { lat: ipLat, lng: ipLng }
        } = ipLocation
        lat = `${ipLat}`
        lng = `${ipLng}`
      }
      const searchParams = new URLSearchParams()
      searchParams.set('keyword', queryKeywords)
      searchParams.set(
        'user_id',
        session.guest ? TEMP_USER_ID : session.user_id
      )
      searchParams.set('lat', lat)
      searchParams.set('lng', lng)
      searchParams.set('country_code', ipLocation?.country_code || '')
      searchParams.set('lang', locale)

      const communities = await fetchSearchCommunities(
        searchParams.toString(),
        locale
      )

      return communities
    }
  })

  const communityList = data || []

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (communityList.length === 0) {
    return (
      <NoQueryResult className='m-auto max-w-[432px] py-[32px] text-center'>
        {t('search.no_result.keyword', {
          search_term: queryKeywords
        })}
      </NoQueryResult>
    )
  }

  return communityList.map((community, index) => {
    const location = community?.location?.levels.join(' ')

    const communityImageUrl = (community.asset as IImageAssetV2)
      ?.image_metadata[0]?.display_url

    return (
      <Link
        key={`community${index}`}
        href={`/community/${community.community_id}`}
        className='block px-[16px] py-[6px] hover:bg-background-2nd'
      >
        <SearchItem
          title={community.title}
          prefix={
            <Image
              className='relative'
              src={communityImageUrl}
              alt='communityImageUrl'
              fill={true}
              fallback={<IconCommunity className='fill-current sq-[24px]' />}
            />
          }
          description={location}
          iconShape='square'
        />
      </Link>
    )
  })
}

export default CommunityResult
